// Boot-failure screen for the /api/config gate (Phase 3b). Deliberately plain
// DOM with inline styles: when this renders, nothing else has booted — no
// React, no router, no theme, no branding. This screen replacing the login
// page when the API is down is the phase's accepted cost (spec §3b): with no
// config there is no auth pool to sign in against, so there is nothing safer
// to render.

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch,
  );
}

export function bootErrorHtml(message: string): string {
  return [
    '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;padding:24px;color:#1f2937;background:#f9fafb">',
    '<div style="max-width:28rem;text-align:center">',
    '<h1 style="font-size:1.25rem;margin:0 0 8px">Something went wrong loading the app</h1>',
    `<p style="color:#6b7280;margin:0 0 16px">${escapeHtml(message)}</p>`,
    '<button type="button" style="padding:8px 20px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer;font-size:0.875rem">Try again</button>',
    '</div></div>',
  ].join('');
}

// Structural root type so this stays testable without a DOM. querySelector is
// optional so the plain `{ innerHTML: '' }` fakes the existing tests use keep
// working; a real element (document.getElementById('root')) has it.
type BootRoot = {
  innerHTML: string;
  querySelector?: (
    selectors: string,
  ) => { addEventListener?: (type: string, listener: () => void) => void } | null;
};

export function renderBootError(root: BootRoot, err: unknown): void {
  const message =
    err instanceof Error && err.message
      ? err.message
      : 'The app configuration could not be loaded.';
  root.innerHTML = bootErrorHtml(message);
  // No CSP blocks an inline onclick today, but this is the one screen a user
  // sees when everything else has already failed — don't make its retry
  // button the thing a future strict CSP silently breaks.
  root.querySelector?.('button')?.addEventListener?.('click', () => location.reload());
}
